import { Injectable } from '@angular/core'
import { CoreService } from './core.service'
import { SchemaUtil } from '../utils'
import { User } from 'depoto-core/src/entities/User'

export interface SessionEntry {
  id: string
  email: string
  clientType: string
  clientCustomUri?: string
  token: {
    accessToken: string
    refreshToken: string
    tokenType?: string
    scope?: string
    expiresIn?: number
    timestamp?: number
  }
  rememberMe: boolean
  company?: any
}

// 'expired' means the server explicitly rejected the refresh token (e.g. invalid_grant) - the
// session is genuinely dead and the user needs to log back in. 'unknown' covers everything else
// (network error, timeout, malformed response) - the token might still be perfectly valid, so
// callers should not present this as an expired session.
export type SessionFailureReason = 'expired' | 'unknown'

export interface SwitchSessionResult {
  ok: boolean
  reason?: SessionFailureReason
}

const PREFIX = 'depoto_client_v2_'
export const SESSIONS_LS_KEY = `${PREFIX}auth_sessions`
const LAST_ACTIVE_LS_KEY = `${PREFIX}last_active_session_id`
const ACTIVE_SS_KEY = `${PREFIX}active_session_id`
// Carries the target session id to a tab opened (new tab/window) from a session-item link -
// sessionStorage is cloned from the opener tab on same-origin link navigation, so without this
// the new tab would just inherit the opener's active session instead of the one the user picked.
const OPEN_SESSION_QUERY_PARAM = 'openSession'

const SERVER_URLS: Record<string, string> = {
  prod: 'https://server1.depoto.cz/',
  stage: 'https://server1.depoto.cz.tomatomstage.cz/',
  dev: 'https://server-dev.depoto.cz/',
}

export interface PendingOpenSessionOutcome {
  entry: SessionEntry
  result: SwitchSessionResult
}

@Injectable({ providedIn: 'root' })
export class MultiSessionService {
  private startupDone = false
  private hasPendingOpenSession = false
  // Kicked off once from the constructor (see below) for a tab opened via buildOpenSessionUrl()
  // (see the session-item link in box-user) - LoginComponent awaits this instead of driving the
  // switchToSession() call itself, because AuthGuardService.canActivate() can flip isAuthenticated
  // true-then-false while this resolves (it unconditionally reloads oauth.session from whatever
  // stale data sits in the shared auth.* storage slot), which destroys and recreates <app-login>
  // (app.component.html gates it on isAuthenticated() directly, outside the router). A field on
  // this instance's constructor promise survives that; local component state does not.
  private pendingOpenSessionOutcome: Promise<PendingOpenSessionOutcome> | null = null
  // True only while the constructor-kicked-off switchToSession() call below is actually in
  // flight. AuthGuardService.canActivate() checks this to skip its own abortAllRequests() call
  // on the tab's first guarded navigation - that navigation fires concurrently with this
  // verification (both happen as part of the same initial bootstrap), and canActivate()'s abort
  // would otherwise cancel our getMyProfile() request mid-flight, turning a perfectly valid
  // session into a false "switch failed". Self-clears once the verification settles, so normal
  // route-to-route navigations still get the intended stale-request cleanup.
  private pendingOpenSessionInFlight = false

  constructor(private core: CoreService) {
    // Must run here, not from onAppStartup() (guard-time, i.e. on first route navigation):
    // <app-login> sits outside the router entirely (app.component.html gates it on
    // isAuthenticated() directly), so LoginComponent can construct and run ngOnInit before the
    // router ever resolves a guarded route. Constructor injection order guarantees this service
    // exists before LoginComponent's does, since LoginComponent injects it too - onAppStartup()
    // gives no such guarantee.
    const targetId = this.applyRequestedSessionFromUrl()
    this.hasPendingOpenSession = !!targetId
    if (targetId) {
      const entry = this.getSessions()[targetId]
      this.pendingOpenSessionInFlight = true
      this.pendingOpenSessionOutcome = this.switchToSession(targetId)
        .then(result => ({ entry, result }))
        .finally(() => {
          this.pendingOpenSessionInFlight = false
        })
    }

    // FetchService reacts to *any* 401 during ordinary use by silently refreshing the access/
    // refresh token in place (OAuthService.getRefreshToken()), entirely outside our control -
    // it reads back whatever refresh_token the server hands it, which is how a server that
    // rotates refresh tokens on use signals the old one is now dead. That path only ever touches
    // the shared auth.* storage slot, never this entry's own copy in auth_sessions - so without
    // this, the *next* explicit switchToSession() call for this same account would hand the
    // server an already-rotated-away refresh token and get a false 'expired', even though the
    // session itself was perfectly fine moments earlier. onLoginEmitter fires on every such
    // refresh (and on a fresh login), so keep the active entry's stored token in step with it.
    this.core.services.oauth.onLoginEmitter.subscribe().then(() => this.syncActiveSessionToken())
  }

  get isPendingOpenSessionInFlight(): boolean {
    return this.pendingOpenSessionInFlight
  }

  private makeSessionId(clientType: string, email: string): string {
    return `${clientType}:${email}`
  }

  private buildTokenEndpoint(clientType: string, customUrl?: string): string {
    const server =
      clientType === 'custom'
        ? customUrl?.endsWith('/')
          ? customUrl
          : `${customUrl || ''}/`
        : SERVER_URLS[clientType] || SERVER_URLS['prod']
    return `${server}oauth/v2/token`
  }

  private getSessions(): Record<string, SessionEntry> {
    try {
      const raw = localStorage.getItem(SESSIONS_LS_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  }

  getSessionsList(): SessionEntry[] {
    return Object.values(this.getSessions())
  }

  getActiveSessionId(): string | null {
    try {
      return sessionStorage.getItem(ACTIVE_SS_KEY)
    } catch {
      return null
    }
  }

  // Session id of the account the live oauth session is signed in as, regardless of which id this
  // tab has recorded as active.
  private getSignedInSessionId(): string | null {
    const oauthSession = this.core.services.oauth.session
    const s = this.core.services.storage
    const email = oauthSession?.email || s.getSync(s.keys.auth.email)
    const clientType = oauthSession?.clientType || s.getSync(s.keys.auth.clientType)
    return email && clientType ? this.makeSessionId(clientType, email) : null
  }

  private setActiveSessionId(id: string): void {
    try {
      sessionStorage.setItem(ACTIVE_SS_KEY, id)
      localStorage.setItem(LAST_ACTIVE_LS_KEY, id)
    } catch {}
  }

  private saveSessions(sessions: Record<string, SessionEntry>): void {
    try {
      localStorage.setItem(SESSIONS_LS_KEY, JSON.stringify(sessions))
    } catch {}
  }

  private upsertSession(entry: SessionEntry): void {
    const sessions = this.getSessions()
    sessions[entry.id] = entry
    this.saveSessions(sessions)
  }

  removeSession(id: string): void {
    const sessions = this.getSessions()
    delete sessions[id]
    this.saveSessions(sessions)
    try {
      if (localStorage.getItem(LAST_ACTIVE_LS_KEY) === id) {
        localStorage.removeItem(LAST_ACTIVE_LS_KEY)
      }
    } catch {}
  }

  /**
   * Called from a window `storage` event when another tab modifies auth_sessions.
   * Returns the session to switch to, null to fully log out, or undefined if no action is needed.
   */
  resolveCrossTabChange(updatedSessionsJson: string | null): SessionEntry | null | undefined {
    const activeId = this.getActiveSessionId()
    if (!activeId) return undefined

    let updated: Record<string, SessionEntry>
    try {
      updated = updatedSessionsJson ? JSON.parse(updatedSessionsJson) : {}
    } catch {
      return undefined
    }

    if (updated[activeId]) return undefined // still present, nothing to do

    const remaining = Object.values(updated)
    return remaining.length > 0 ? remaining[0] : null
  }

  saveCurrentSession(): void {
    const oauthSession = this.core.services.oauth.session
    if (!oauthSession?.email || !oauthSession?.clientType || !oauthSession.isAuthenticated) return
    const id = this.makeSessionId(oauthSession.clientType, oauthSession.email)
    const entry: SessionEntry = {
      id,
      email: oauthSession.email,
      clientType: oauthSession.clientType,
      token: {
        accessToken: oauthSession.token?.accessToken,
        refreshToken: oauthSession.token?.refreshToken,
        tokenType: oauthSession.token?.tokenType,
        scope: oauthSession.token?.scope,
        expiresIn: oauthSession.token?.expiresIn,
        timestamp: oauthSession.token?.timestamp || Date.now(),
      },
      rememberMe: oauthSession.isRememberMe ?? true,
      company: this.core.services.storage.getSync(this.core.services.storage.keys.auth.company),
    }
    this.upsertSession(entry)
    this.setActiveSessionId(id)
  }

  onAppStartup(): void {
    if (this.startupDone) return
    this.startupDone = true
    this.migrateFromLegacy()
    if (!this.hasPendingOpenSession) {
      this.restoreSessionForTab()
    }
  }

  // Builds a URL that, when opened in a new tab/window, makes that tab boot into the given
  // session instead of whichever session the opener tab currently has active.
  buildOpenSessionUrl(sessionId: string): string {
    try {
      const url = new URL(window.location.origin + '/')
      url.searchParams.set(OPEN_SESSION_QUERY_PARAM, sessionId)
      return url.toString()
    } catch {
      return window.location.origin
    }
  }

  // Not consumed/nulled - a tab can remount <app-login> more than once during bootstrap (see the
  // constructor comment above), and every instance needs to be able to pick up the same outcome.
  getPendingOpenSessionOutcome(): Promise<PendingOpenSessionOutcome> | null {
    return this.pendingOpenSessionOutcome
  }

  private applyRequestedSessionFromUrl(): string | null {
    try {
      const url = new URL(window.location.href)
      const targetId = url.searchParams.get(OPEN_SESSION_QUERY_PARAM)
      if (!targetId) return null

      url.searchParams.delete(OPEN_SESSION_QUERY_PARAM)
      history.replaceState(history.state, '', url.toString())

      if (!this.getSessions()[targetId]) return null

      // depoto-core's OAuthService constructor already ran (and loaded whatever the shared auth.*
      // storage slot or this tab's opener-cloned sessionStorage happened to hold) before this
      // runs. Force the login screen instead of flashing that unrelated session - switchToSession()
      // (kicked off right after, see constructor) verifies and switches to the actually-requested
      // one, and its own result is what LoginComponent ends up displaying.
      this.core.services.oauth.session.isAuthenticated = false
      return targetId
    } catch {
      return null
    }
  }

  private migrateFromLegacy(): void {
    try {
      const sessions = this.getSessions()
      if (Object.keys(sessions).length > 0) return

      const s = this.core.services.storage
      const tokenData = s.getSync(s.keys.auth.data)
      const email = s.getSync(s.keys.auth.email)
      const clientType = s.getSync(s.keys.auth.clientType)
      const isAuthenticated = s.getSync(s.keys.auth.isAuthenticated)
      const rememberMe = s.getSync(s.keys.auth.rememberMe)
      const company = s.getSync(s.keys.auth.company)

      if (!tokenData?.accessToken || !email || !clientType || !isAuthenticated) return

      const id = this.makeSessionId(clientType, email)
      this.upsertSession({ id, email, clientType, token: { ...tokenData }, rememberMe: rememberMe || false, company })
      this.setActiveSessionId(id)
    } catch {}
  }

  private restoreSessionForTab(): void {
    try {
      const activeId = sessionStorage.getItem(ACTIVE_SS_KEY)
      const targetId = activeId || localStorage.getItem(LAST_ACTIVE_LS_KEY)
      if (!targetId) return

      const sessions = this.getSessions()
      const entry = sessions[targetId]
      if (!entry?.token?.accessToken) return

      const s = this.core.services.storage
      const currentEmail = s.getSync(s.keys.auth.email)
      const currentClientType = s.getSync(s.keys.auth.clientType)

      // Compare the full clientType:email identity, not email alone — shared auth.* storage is a
      // single cross-tab slot (depoto-core only holds one session in memory), so another tab can
      // overwrite it with a different session at any time. Two accounts on different servers can
      // share the same email, so an email-only match here would wrongly treat a same-email session
      // from another server as "already correct" and skip restoring this tab's actual target entry.
      if (currentEmail === entry.email && currentClientType === entry.clientType) {
        if (!activeId) sessionStorage.setItem(ACTIVE_SS_KEY, targetId)
        // Ensure in-memory session is fully restored even when storage is already correct —
        // oauth.logout() clears auth_is_authenticated but not our session map, so isAuthenticated
        // can be false in localStorage while we still have a valid stored token.
        this.core.services.oauth.loadDataSync()
        this.core.services.oauth.session.isAuthenticated = true
        return
      }

      // Restore session data into the shared localStorage auth keys
      s.set(s.keys.auth.clientType, entry.clientType)
      s.set(s.keys.auth.email, entry.email)
      s.set(s.keys.auth.rememberMe, entry.rememberMe)
      s.set(s.keys.auth.isAuthenticated, true)
      s.set(s.keys.auth.data, entry.token)
      if (entry.company) s.set(s.keys.auth.company, entry.company)

      // Re-sync depoto-core in-memory session from the updated localStorage
      this.core.services.oauth.loadDataSync()
      this.core.services.oauth.session.isAuthenticated = true
      if (!activeId) sessionStorage.setItem(ACTIVE_SS_KEY, targetId)
    } catch {}
  }

  // Fetch a token for a new account without disturbing the current in-memory session
  async addAccountLogin(
    email: string,
    password: string,
    clientType: string,
    customUrl?: string
  ): Promise<SessionEntry | null> {
    try {
      const defaultKeys = this.core.services.oauth.defaultClientKeys
      const clientKeys = defaultKeys[clientType] || defaultKeys['prod']
      const endpoint = this.buildTokenEndpoint(clientType, customUrl)

      const body = new FormData()
      body.append('client_id', clientKeys.clientId)
      body.append('client_secret', clientKeys.clientSecret)
      body.append('grant_type', 'password')
      body.append('username', email)
      body.append('password', password)

      const resp = await fetch(endpoint, { method: 'post', body })
      const data = await resp.json()
      if (!data.access_token || data.error) return null

      const id = this.makeSessionId(clientType, email)
      const entry: SessionEntry = {
        id,
        email,
        clientType,
        clientCustomUri: customUrl,
        token: {
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          tokenType: data.token_type,
          scope: data.scope,
          expiresIn: data.expires_in,
          timestamp: Date.now(),
        },
        rememberMe: true,
      }
      this.upsertSession(entry)
      return entry
    } catch {
      return null
    }
  }

  async switchToSession(id: string): Promise<SwitchSessionResult> {
    try {
      // Kill any requests still in flight from the outgoing session before swapping tokens.
      // Without this, a stale response can land after the swap and race against the new
      // session's in-memory state (e.g. trigger a spurious refresh/logout via a 401 that
      // belongs to the old session), leaving isBaseModelLoaded stuck false and the router
      // outlet permanently blank until a hard reload.
      this.core.services.http.abortAllRequests()
      const sessions = this.getSessions()
      const currentActiveId = this.getActiveSessionId()
      // Only roll back to the previous session on failure if we were actually authenticated into
      // it - e.g. switching between accounts from the box-user dropdown. On the login screen
      // we're never authenticated to begin with, so currentActiveId can just be a stale leftover
      // from a session that expired without going through logoutCurrentSession() (e.g. a silent
      // 401 elsewhere). Rolling back to that unverified entry would force-mark the app
      // "authenticated" with a token that's likely just as dead, leaving the user stuck on a
      // blank page instead of on the login screen.
      const wasAuthenticated = this.core.services.oauth.isAuthenticated()
      const previousEntry = wasAuthenticated && currentActiveId ? sessions[currentActiveId] : undefined
      // Only persist the current session if it still exists — if another tab removed it,
      // calling saveCurrentSession() here would re-add it from stale in-memory state.
      if (!currentActiveId || sessions[currentActiveId]) {
        this.saveCurrentSession()
      }
      const entry = sessions[id]
      if (!entry?.token?.accessToken) return { ok: false, reason: 'unknown' }

      const result = await this.applySession(entry)
      if (!result.ok) {
        // Keep the entry in the sessions list either way — it's the user's login history, and on
        // a genuine 'expired' they may just want to log back into it. Roll back to whatever was
        // active before, so the app isn't left "authenticated" with a token that will 401 on every
        // subsequent request. Restore it directly from the already-known-good entry rather than
        // re-verifying over the network: that second call can itself be flaky (right after an
        // abort + a 401) and get the app stuck "unauthenticated" even though the previous session
        // was perfectly fine.
        if (previousEntry && previousEntry.id !== id) {
          this.restoreSession(previousEntry)
        }
        return result
      }
      return { ok: true }
    } catch {
      return { ok: false, reason: 'unknown' }
    }
  }

  // Logs out of the currently active account only. Deliberately does not auto-switch into
  // another remembered session — that's a choice the login screen now offers the user
  // explicitly (it lists MultiSessionService.getSessionsList()), rather than the app silently
  // picking one for them.
  //
  // forgetSignedInAccount (an explicit sign-out) also removes the entry for the account that is
  // actually signed in. This tab's active session id can be missing or point elsewhere - e.g.
  // restoreSessionForTab() bails out without setting it when last_active_session_id is missing or
  // stale, while the tab still runs on the shared auth.* slot - and removing only by that id left
  // the signed-out account remembered on the login page. Not used for cross-tab removals, where
  // the in-memory session may already belong to a different account.
  async logoutCurrentSession(options: { forgetSignedInAccount?: boolean } = {}): Promise<void> {
    this.core.services.http.abortAllRequests()
    const activeId = this.getActiveSessionId()
    if (activeId) {
      this.removeSession(activeId)
    }
    if (options.forgetSignedInAccount) {
      const signedInId = this.getSignedInSessionId()
      if (signedInId && signedInId !== activeId) {
        this.removeSession(signedInId)
      }
    }
    try {
      sessionStorage.removeItem(ACTIVE_SS_KEY)
    } catch {}
    this.core.services.oauth.logout()
  }

  // Writes an entry's token/account data into shared storage and the in-memory oauth session
  // without verifying it over the network — used both to restore a session we already know is
  // good (markAuthenticated: true, the default) and to stage a candidate session for
  // applySession() to verify before committing to it (markAuthenticated: false).
  //
  // Staging must NOT flip isAuthenticated to true up front: app.component.html gates <app-login>
  // behind a structural `*ngIf="!isAuthenticated()"`, so a premature true-then-false flip while
  // a switch attempt is still being verified destroys and recreates the whole component,
  // silently wiping out any state a caller sets on it afterwards (e.g. LoginComponent's re-auth
  // lock/prefill after a failed switch — the assignment lands on the now-orphaned old instance).
  private restoreSession(entry: SessionEntry, markAuthenticated = true): void {
    const s = this.core.services.storage
    s.set(s.keys.auth.clientType, entry.clientType)
    s.set(s.keys.auth.email, entry.email)
    s.set(s.keys.auth.rememberMe, entry.rememberMe)
    s.set(s.keys.auth.data, entry.token)
    if (entry.company) s.set(s.keys.auth.company, entry.company)
    if (entry.clientCustomUri) s.set(s.keys.auth.customServerUrl, entry.clientCustomUri)

    this.core.services.oauth.loadDataSync()
    if (markAuthenticated) {
      s.set(s.keys.auth.isAuthenticated, true)
      this.core.services.oauth.session.isAuthenticated = true
    }
    this.setActiveSessionId(entry.id)
  }

  // Refreshes entry.token in place via a raw, isolated fetch - unlike depoto-core's
  // oauth.getRefreshToken()/getMyProfile(), this does NOT go through the shared FetchService,
  // which reacts to *any* 401 by firing its own unawaited refresh-then-maybe-logout
  // (FetchService.ts post()/get()). If that races and its own refresh also fails, it calls
  // oauth.logout() directly, wiping all auth storage and the in-memory user - completely outside
  // our control. Doing it here also lets us tell a genuine server rejection (the refresh token is
  // actually dead) apart from a transient failure (network error, timeout, bad gateway) instead of
  // collapsing both into the same boolean the way oauth.getRefreshToken() does.
  private async refreshEntryToken(entry: SessionEntry): Promise<'ok' | SessionFailureReason> {
    try {
      const defaultKeys = this.core.services.oauth.defaultClientKeys
      const clientKeys = defaultKeys[entry.clientType] || defaultKeys['prod']
      const endpoint = this.buildTokenEndpoint(entry.clientType, entry.clientCustomUri)

      const body = new FormData()
      body.append('client_id', clientKeys.clientId)
      body.append('client_secret', clientKeys.clientSecret)
      body.append('grant_type', 'refresh_token')
      body.append('refresh_token', entry.token.refreshToken)

      const resp = await fetch(endpoint, { method: 'post', body })
      let data: any
      try {
        data = await resp.json()
      } catch {
        // Not valid JSON (e.g. a proxy/error page) - we don't have a confirmed rejection.
        return 'unknown'
      }
      // Only an explicit OAuth error (e.g. invalid_grant) proves the refresh token is actually dead.
      if (data?.error) return 'expired'
      if (!resp.ok || !data?.access_token) return 'unknown'

      entry.token = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenType: data.token_type,
        scope: data.scope,
        expiresIn: data.expires_in,
        timestamp: Date.now(),
      }
      return 'ok'
    } catch {
      // fetch() itself threw - offline, timeout, DNS, CORS, etc. Not proof the token is dead.
      return 'unknown'
    }
  }

  // True when the entry's access token is at (or within a 60s skew of) the end of its own stated
  // lifetime, or when there is no timestamp/expiresIn bookkeeping to judge by (legacy entries) -
  // in which case we conservatively treat it as stale and refresh. The skew keeps a switch from
  // starting with a token that expires mid-flight.
  private isAccessTokenStale(entry: SessionEntry): boolean {
    const issuedAt = entry.token?.timestamp ?? 0
    const lifetimeMs = (entry.token?.expiresIn ?? 0) * 1000
    if (!issuedAt || lifetimeMs <= 0) return true
    return Date.now() >= issuedAt + lifetimeMs - 60_000
  }

  // Returns a failure reason when the switch didn't go through, so the caller can roll back and
  // only present the session as truly "expired" when we actually confirmed that with the server.
  private async applySession(entry: SessionEntry): Promise<SwitchSessionResult> {
    this.restoreSession(entry, false)

    // Refresh up front only when the access token is actually spent. When it IS, doing it here
    // via the isolated path - before the profile reload below ever touches the shared, reactive
    // FetchService - matters: reloadUserProfile()'s getMyProfile() goes through FetchService,
    // which reacts to *any* 401 with its own unawaited refresh-then-maybe-logout (see
    // refreshEntryToken's comment below). That reaction is a fire-and-forget race we cannot
    // observe or cancel from here - even after we recover, FetchService's own doomed refresh
    // (already in flight against the same dead token) can still call oauth.logout() moments later
    // and silently undo our rollback, forcing the whole app to /login. Refreshing a stale token
    // here first means reloadUserProfile() never hands FetchService a token that can 401.
    //
    // But a *still-valid* access token needs no refresh, and forcing one anyway is actively
    // harmful for a session whose refresh token is single-use and may already be spent elsewhere
    // (an external-login hand-off carries exactly such a token - the issuing app keeps using it):
    // that refresh comes back invalid_grant and takes an otherwise-perfectly-usable session down
    // with it. So when the access token is still within its own lifetime, skip the refresh and
    // let reloadUserProfile() below be the real check - a live token returns 200 there and the
    // switch succeeds; a genuinely dead one just fails the switch as 'unknown'.
    if (entry.rememberMe && this.isAccessTokenStale(entry)) {
      const result = await this.refreshEntryToken(entry)
      if (result !== 'ok') {
        this.core.services.oauth.session.isAuthenticated = false
        return { ok: false, reason: result }
      }
      // Re-sync storage/in-memory oauth session with the freshly refreshed token before persisting it.
      this.restoreSession(entry, false)
      this.persistRefreshedToken(entry.id)
    }

    const profileLoaded = await this.reloadUserProfile(entry.id)
    if (!profileLoaded) {
      this.core.services.oauth.session.isAuthenticated = false
      // getMyProfile() failures aren't distinguishable from a network error here, so don't claim
      // the session is expired - just fail the switch.
      return { ok: false, reason: 'unknown' }
    }
    this.core.services.storage.set(this.core.services.storage.keys.auth.isAuthenticated, true)
    this.core.services.oauth.session.isAuthenticated = true
    return { ok: true }
  }

  private persistRefreshedToken(sessionId: string): void {
    const sessions = this.getSessions()
    const entry = sessions[sessionId]
    if (!entry) return
    const token = this.core.services.oauth.session.token
    entry.token = {
      accessToken: token.accessToken,
      refreshToken: token.refreshToken,
      tokenType: token.tokenType,
      scope: token.scope,
      expiresIn: token.expiresIn,
      timestamp: token.timestamp || Date.now(),
    }
    this.saveSessions(sessions)
  }

  // Called on every onLoginEmitter firing (fresh login and every silent 401-triggered refresh
  // alike) - see the constructor's subscription for why. Only ever touches the entry that's
  // actually both the recorded active session AND a genuine identity match for the live oauth
  // session; either mismatch is a sign we're in the brief window before a fresh login or switch
  // has finished pointing the active session id at this account, so skip rather than risk
  // writing a freshly-issued token into the wrong (or a stale, not-yet-updated) entry.
  private syncActiveSessionToken(): void {
    const activeId = this.getActiveSessionId()
    if (!activeId) return
    const entry = this.getSessions()[activeId]
    const oauthSession = this.core.services.oauth.session
    if (!entry || !oauthSession?.email || !oauthSession.token?.refreshToken) return
    if (entry.email !== oauthSession.email || entry.clientType !== oauthSession.clientType) return
    this.persistRefreshedToken(activeId)
  }

  private async reloadUserProfile(sessionId: string): Promise<boolean> {
    try {
      const user: User = await this.core.services.user.getMyProfile(SchemaUtil.userProfile)
      if (!user?.email) return false
      this.core.services.storage.set(
        this.core.services.storage.keys.auth.companyCarriers,
        user.company.carrierRelations
      )
      // Persist company info into auth_sessions so it survives page reload
      const sessions = this.getSessions()
      if (sessions[sessionId]) {
        sessions[sessionId].company = user.company
        this.saveSessions(sessions)
      }
      this.core.services.user.afterSetCurrentUser(user)
      return true
    } catch {
      return false
    }
  }
}
